import { Box, SxProps, Theme, styled } from "@mui/material";
import { transparentize } from "polished";
import React from "react";

import { prefixClassName } from "@smartrr/shared/components/primitives";

import { DotSpinner } from "./DotSpinner";
import { ICustomerPortalTheme } from "../../entities/CustomerPortalTheme";

interface QuantityModifyContainerProps {
  theme?: Theme & ICustomerPortalTheme;
}

export const QuantityModifyContainer = styled(Box)<QuantityModifyContainerProps>`
  align-items: center;
  background-color: ${props => props.theme?.modernSecondaryButtonColor};
  border: ${props => props.theme?.modernButtonBorderWidth}px solid
    ${props => props.theme?.modernSecondaryButtonBorderColor};
  border-radius: ${props => props.theme?.modernButtonBorderRadius}px;
  color: ${props => props.theme?.modernSecondaryButtonFontColor};
  display: flex;
  flex-grow: 1;
  font-family: ${props => props.theme?.modernBodyFontFamily};
  font-size: ${props => props.theme?.modernBodyFontSize}px;
  height: 30px;
  justify-content: space-between;
  margin: 7px 0;
  outline: none;
  overflow: hidden;
  padding: 0;
  transition: opacity 0.15s ease-in-out;
  white-space: nowrap;
  width: 100%;

  > * {
    color: ${props => props.theme?.modernSecondaryButtonFontColor};
    flex: 1;

    &:first-of-type {
      border: none;
      border-radius: 0;
    }

    &:nth-child(2) {
      border: none;
    }

    &:last-child {
      border: none;
      border-radius: 0;
    }
  }

  > .smartrr-portal-quantity-button,
  > .smartrr-portal-quantity-number {
    margin-left: 0;
    margin-right: 0;
  }
`;

interface QuantityNumberProps {
  disabled?: boolean;
  theme?: Theme & ICustomerPortalTheme;
}

export const QuantityNumber = styled("div")<QuantityNumberProps>`
  align-items: center;
  align-self: center;
  display: flex;
  font-family: ${props => props.theme.modernBodyFontFamily};
  font-size: ${props => props.theme.modernBodyFontSize}px;
  font-weight: 400;
  justify-content: center;
  margin: 0 16.5px;
  text-align: center;
`;

interface QuantityButtonProps {
  disabled?: boolean;
  theme?: Theme & ICustomerPortalTheme;
}

export const QuantityButton = styled("button")<QuantityButtonProps>`
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-family: ${props => props.theme.modernBodyFontFamily};
  font-size: ${props => props.theme.modernBodyFontSize}px;
  font-weight: 400;
  outline: none;
  pointer-events: ${props => (props.disabled ? "none" : "auto")};
`;

interface StyledDecrementProps {
  disabled?: boolean;
  theme?: Theme & ICustomerPortalTheme;
}

export const StyledDecrement = styled(QuantityButton)<StyledDecrementProps>`
  color: ${({ theme, disabled }) =>
    theme?.modernSecondaryButtonFontColor
      ? transparentize(disabled ? 0.5 : 0, theme?.modernSecondaryButtonFontColor)
      : theme?.modernSecondaryButtonFontColor};
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
  transition: opacity 0.15s ease-in-out;
`;

interface AquariusQuantityProps {
  value: number;
  className?: string;
  isLoading?: boolean;
  maxQty?: number;

  onChange(quantity: number): void;

  sx?: SxProps<Theme>;
}

export function AquariusQuantity({
  isLoading = false,
  onChange,
  value,
  maxQty,
  className = "",
  sx,
}: AquariusQuantityProps): React.JSX.Element {
  if (value < 0) {
    throw new Error("quantity received negative number");
  }

  const onQuantityChange = React.useCallback(
    (delta: number) => {
      if (maxQty && maxQty > 0) {
        if ((delta === 1 && value + delta <= maxQty) || delta === -1) {
          onChange(value + delta);
        }
      } else {
        onChange(value + delta);
      }
    },
    [value]
  );

  return (
    <QuantityModifyContainer className={prefixClassName("quantity-container", className)} sx={sx}>
      <StyledDecrement
        className={prefixClassName("quantity-button")}
        name="minus"
        disabled={isLoading || value === 0}
        onClick={isLoading ? () => null : () => onQuantityChange(-1)}
      >
        －
      </StyledDecrement>
      <QuantityNumber className={prefixClassName("quantity-number")}>
        {isLoading ? <DotSpinner size={10} /> : value}
      </QuantityNumber>
      <QuantityButton
        className={prefixClassName("quantity-button")}
        disabled={isLoading}
        onClick={isLoading ? () => null : () => onQuantityChange(1)}
      >
        ＋
      </QuantityButton>
    </QuantityModifyContainer>
  );
}
